import type { ReactNode, InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';

// ── FormField wrapper ─────────────────────────────────────────────────────────

interface FormFieldProps {
  label?:    string;
  required?: boolean;
  hint?:     string;
  error?:    string;
  children:  ReactNode;
  className?: string;
}

export function FormField({ label, required, hint, error, children, className }: FormFieldProps) {
  return (
    <div className={`form-group${className ? ` ${className}` : ''}`}>
      {label && (
        <label>
          {label}
          {required && <span style={{ color: 'var(--danger)', marginLeft: 3 }}>*</span>}
        </label>
      )}
      {children}
      {error
        ? <p className="form-hint" style={{ color: 'var(--danger)', marginTop: 4 }}>{error}</p>
        : hint
        ? <p className="form-hint">{hint}</p>
        : null
      }
    </div>
  );
}

// ── Input ─────────────────────────────────────────────────────────────────────

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  prefix?: string;
  suffix?: string;
}

export function Input({ prefix, suffix, className, ...props }: InputProps) {
  if (prefix || suffix) {
    return (
      <div className={`input-wrap${prefix ? ' has-prefix' : ''}${suffix ? ' has-suffix' : ''}`}>
        {prefix && <span className="input-prefix">{prefix}</span>}
        <input className={className} {...props} />
        {suffix && <span className="input-suffix">{suffix}</span>}
      </div>
    );
  }
  return <input className={className} {...props} />;
}

// ── Select ────────────────────────────────────────────────────────────────────

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  placeholder?: string;
  options: { value: string; label: string }[];
}

export function Select({ placeholder, options, ...props }: SelectProps) {
  return (
    <select {...props}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

// ── Textarea ──────────────────────────────────────────────────────────────────

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} />;
}

// ── CalcField — read-only calculated display ──────────────────────────────────

interface CalcFieldProps {
  value: string;
  highlight?: boolean;
  hint?: string;
}

export function CalcField({ value, highlight, hint }: CalcFieldProps) {
  return (
    <>
      <div
        className="calc-field"
        style={highlight ? { fontSize: 15, fontWeight: 800, color: 'var(--primary)' } : undefined}
      >
        {value}
      </div>
      {hint && <p className="form-hint">{hint}</p>}
    </>
  );
}
